import { useState, useCallback, useMemo, useEffect, Fragment } from "react";
import { useAppSelector } from "redux/hooks";
import {
  ProfileAccountInfo,
  SuspendedPayload,
} from "api/interfaces/accountInterface.interface";
import {
  BasePagination,
  CustomError,
  LEVEL_TYPE,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { TableColumnsType } from "antd";
import {
  changeSortOrderAntd,
  CommonTable,
  ExpandTableContainer,
  onSortAntd,
} from "components/atoms/table/AntdTable";
import {
  CpRequestInfo,
  EuRequestType,
  SubscriptionOrgListColumnType,
  SubscriptionOrgListResult,
  SubscriptionPageOutletContext,
  SusbcriptonOrgListResponse,
} from "api/interfaces/subscription.interface";
import TableButton from "components/atoms/buttons/TableButton";
import { RowMobileNoColumn } from "styles/ModalCommonStyled";
import { ReactComponent as CardSVG } from "assets/icons/card.svg";
import { useMenu } from "hook/useMenu";
import {
  changeLevelFromABBToABB,
  formatNumber,
  formatUppercaseOnly,
} from "utils/functions";
import { useQuery } from "react-query";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import { getAccountTimeFormatDate } from "utils/timeUtil";
import { getSubscriptonOrgList } from "api/subscriptionAPI";
import { SubscriptionOrgStyled } from "pages/subscription/SubscriptionStyled";

import useApiError from "hook/useApiError";
import {
  Navigate,
  useLocation,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import {
  checkSubscriptionStatusFromExpireDate,
  getActivateStatus,
  getExpiredTooltipText,
  isPendingUsagePlanSwitch,
  USAGE_PLAN_SWITCH_WARNING_TEXT,
} from "utils/SubscriptionUtil";
import ActivatedChannel from "pages/subscription/modal/common/ActivatedChannel";
import StatusAlertBox from "pages/subscription/modal/common/StatusAlertBox";
import { WithTooltip } from "components/blocks/WithTooltip";
import { IoAlertCircle } from "react-icons/io5";
import CpRequestCheckModal from "pages/subscription/modal/requestKey/list/cpRequest";
import SubscriptionActivateModal from "pages/subscription/modal/activate";
import SwitchToUsagePlanModal from "pages/subscription/modal/autoEnroll";
import * as mediaQuery from "components/MediaQuery";
import RBACWrapper from "components/blocks/function/RBACWrapper";
import SuspendButton from "./SuspendButton";
import { ExpandableConfig } from "antd/es/table/interface";
import SubscriptionExpandOrgList from "./SubscriptionExpandOrgList";
import { BsArrowsCollapse } from "react-icons/bs";
import { MdOutlineExpand } from "react-icons/md";
import AccountId from "components/atoms/text/labels/AccountId";
import { NameLabel } from "pages/account/AccountStyled";
import useStompDebouncedRefetch from "hook/useStompDebouncedRefetch";
import { suspendOptions } from "utils/options";
import DeactivateModal from "components/blocks/modal/deactivate/DeactivateModal";
import useStatusMutation from "hook/useStatusMutation";
import { postUpdateSuspension } from "api/accountAPI";
import { useQueryClient } from "react-query";
import ToggleComponent from "components/atoms/toggle/Toggle";
import { checkIsSubCP } from "utils/AccountUtil";
import { postAutoEnroll } from "api/planAPI";
import { BILLING_TYPE } from "api/interfaces/planInterface.interface";
import { FaCheck } from "react-icons/fa";
import { useFlag } from "@unleash/proxy-client-react";

type Props = {};
type AutoEnrollModalState = {
  row: SubscriptionOrgListColumnType;
  checked: boolean;
};

const summarizeRequestCounts = (requests: EuRequestType[]): React.ReactNode => {
  const counts = requests.reduce<Record<string, number>>((acc, cur) => {
    acc[cur.keyType] = (acc[cur.keyType] || 0) + 1;
    return acc;
  }, {});

  return Object.entries(counts).map(([keyType, count], index) => {
    return (
      <Fragment key={index}>
        <span>
          {count} {formatUppercaseOnly(keyType)}{" "}
          {index < Object.entries(counts).length - 1 && <>&&nbsp;</>}
        </span>
      </Fragment>
    );
  });
};

export default function SubscriptionOrgList(props: Props): JSX.Element {
  const isMobile = mediaQuery.useIsMobile();
  const isDesktop = mediaQuery.useIsDesktop();
  const isBigScreen = mediaQuery.useIsBigScreen();
  const [selectedValue, setSelectedValue] = useState<Omit<
    SuspendedPayload,
    "accountId"
  > | null>(null);
  const selectedOption = useMemo(() => {
    if (!selectedValue) return null;
    return suspendOptions.find(
      (option) => option.value === selectedValue.suspensionStatus
    );
  }, [selectedValue]);

  const [pageInfo, setPageInfo] = useState<Partial<BasePagination>>({});

  const { onHeaderCell, onChangePage, setStatus, setButtons } =
    useOutletContext<SubscriptionPageOutletContext>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const queryInfo = useMemo<BasePagination>(() => {
    const get = (key: string) => searchParams.get(key);

    return {
      ...baseQueryInfo,
      ...pageInfo,
      ...(get("pageNum") && { pageNum: Number(get("pageNum")) }),
      ...(get("pageLimit") && { pageLimit: Number(get("pageLimit")) }),
      ...{ keyword: get("keyword") ?? "" },
      ...(get("sortType") && { sortType: get("sortType")! }),
      ...(get("sortDirection") && {
        sortDirection: get("sortDirection") as SORT_DIRECTION,
      }),
      ...(get("type") && { type: get("type") }),
    };
  }, [searchParams, pageInfo]);
  const { debouncedNavigate } = useMenu({});
  const { handleErrorNotify, lang, notifySuccess } = useApiError();

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const queryClient = useQueryClient();
  const isDW = selectedAccount.accountLevel === LEVEL_TYPE.DW;
  const isCP = selectedAccount.accountLevel === LEVEL_TYPE.CP;
  const isSubCp = checkIsSubCP(selectedAccount);
  const isPlanHide = useFlag("feature_plan_hide");

  const [isModalActivate, setIsModalActivate] =
    useState<SubscriptionOrgListColumnType | null>(null);
  const [isUsagePlanModalOpen, setIsUsagePlanModalOpen] =
    useState<AutoEnrollModalState | null>(null);

  const [cpRequestInfo, setCpRequestInfo] = useState<CpRequestInfo | null>(
    null
  );
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const queryKey = useQueryKey(queryInfo);
  const isRowExpandable = useCallback(
    (record: SubscriptionOrgListColumnType) =>
      record.hasAccounts > 0 && record.level !== LEVEL_TYPE.EU,
    []
  );
  const { data, isFetching, refetch } = useQuery(
    ["getSubscriptonOrgList", queryKey, selectedAccount.accountId],
    () =>
      getSubscriptonOrgList({
        accountId: selectedAccount.accountId,
        payload: queryInfo,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: SusbcriptonOrgListResponse) => {
        if (res.page) {
          setPageInfo((query) => {
            return {
              ...query,
              ...res.page,
            };
          });
          if (setStatus) {
            setStatus({
              totalTitle: "Total",
              totalValue: res.page.total,
            });
          }
        }
      },
      onError: (_e: any) => {
        handleErrorNotify(_e, lang.label.subscription.org.list.fail);
      },
    }
  );
  useStompDebouncedRefetch({
    selector: (state) =>
      ({
        organizationListUpdated: state.stomp.organizationListUpdated,
        subscriptionRequestUpdated:
          state.subscriptionStomp.subscriptionRequestUpdated,
        subscriptionConfirmUpdated:
          state.subscriptionStomp.subscriptionConfirmUpdated,
        subscriptionActivatedUpdated:
          state.subscriptionStomp.subscriptionActivatedUpdated,
        planAutoEnrollUpdated: state.subscriptionStomp.planAutoEnrollUpdated,
      } as Record<string, unknown>),
    stompKey: [
      "organizationListUpdated",
      "subscriptionRequestUpdated",
      "subscriptionConfirmUpdated",
      "subscriptionActivatedUpdated",
      "planAutoEnrollUpdated",
    ],

    refetch: refetch,
  });

  const mutationUpdateSuspension = useStatusMutation(postUpdateSuspension, {
    onSuccess: (res: any) => {
      notifySuccess(lang.label.billing.notify.update.success);
      setSelectedValue(null);
      queryClient.invalidateQueries("getSubscriptonOrgList");
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.billing.notify.update.fail);
    },
  });
  const onConfirmSuspend = useCallback(
    (targetAccountId: string, value: number) => {
      if (!mutationUpdateSuspension.idle) {
        return;
      }
      mutationUpdateSuspension.mutate({
        accountId: selectedAccount.accountId,
        targetAccountId: targetAccountId,
        suspensionStatus: value,
      });
    },
    [mutationUpdateSuspension, selectedAccount.accountId]
  );
  const onChangeStatus = useCallback(
    (payload: Omit<SuspendedPayload, "accountId">) => {
      setSelectedValue(payload);
    },
    []
  );
  const mutationAutoEnroll = useStatusMutation(postAutoEnroll, {
    onSuccess: () => {
      setIsUsagePlanModalOpen(null);
      refetch();
    },
    onError: () => {},
  });
  const executeAutoEnroll = useCallback(
    (row: SubscriptionOrgListColumnType, checked: boolean) => {
      if (!mutationAutoEnroll.idle) return;

      const payload = {
        accountId: row.accountId,
        autoEnroll: checked,
        isNow: row.channelExpire ? false : true,
      };
      mutationAutoEnroll.mutate({
        accountId: selectedAccount.accountId,
        payload,
      });
    },
    [mutationAutoEnroll, selectedAccount.accountId]
  );
  const onChangeAutoEnroll = useCallback(
    (row: SubscriptionOrgListColumnType, checked: boolean) => {
      if (checked) {
        setIsUsagePlanModalOpen({ row, checked });
        return;
      }
      executeAutoEnroll(row, checked);
    },
    [executeAutoEnroll]
  );
  const dataSource: SubscriptionOrgListColumnType[] = useMemo(() => {
    // console.log(data, data?.result);
    if (data && data?.result && data?.error === 0) {
      return data?.result?.map((res: SubscriptionOrgListResult) => {
        return {
          ...res,
          key: res.accountId,
        };
      });
    }
    return [];
  }, [data]);

  const onModalRequest = (row?: SubscriptionOrgListColumnType) => {
    setCpRequestInfo(
      row
        ? {
            accountId: row.accountId,
            accountName: row.name,
            accountNumber: row.accountNumber,
            accountLevel: row.level,
          }
        : null
    );
  };
  const expandableRowKeysInPage = useMemo(() => {
    return (dataSource ?? [])
      .filter(isRowExpandable)
      .map((row) => row.accountId);
  }, [dataSource, isRowExpandable]);

  const isAllExpandedInPage = useMemo(() => {
    if (expandableRowKeysInPage.length === 0) return false;

    // 페이지에서 펼칠 수 있는 것들이 모두 expandedRowKeys에 포함되어 있으면 true
    return expandableRowKeysInPage.every((key) =>
      expandedRowKeys.includes(key)
    );
  }, [expandableRowKeysInPage, expandedRowKeys]);

  const onToggleExpandAllInPage = useCallback(() => {
    setExpandedRowKeys((prev) => {
      if (expandableRowKeysInPage.length === 0) return prev;

      // 이미 전부 펼쳐져 있으면 접기(해당 페이지 것만 제거)
      if (expandableRowKeysInPage.every((key) => prev.includes(key))) {
        return prev.filter((key) => !expandableRowKeysInPage.includes(key));
      }

      // 아니면 펼치기(기존 + 페이지 key 합집합)
      const merged = new Set([...prev, ...expandableRowKeysInPage]);
      return Array.from(merged);
    });
  }, [expandableRowKeysInPage]);
  const columns: TableColumnsType<SubscriptionOrgListColumnType> = [
    {
      key: "accountNumber",
      dataIndex: "accountNumber",
      title: "ID",
      render: (value, row) => {
        return (
          <AccountId
            accountId={value}
            level={changeLevelFromABBToABB(row) as LEVEL_TYPE}
          />
        );
      },
      width: 95,
      sorter: true,
      sortOrder: changeSortOrderAntd(queryInfo, "accountNumber"),
      onHeaderCell: onHeaderCell,
      hidden: !isDesktop,
    },
    {
      key: "name",
      title: "Name",
      render: (value, row) => (
        <>
          {!isDesktop && (
            <AccountId
              accountId={row.accountNumber}
              level={changeLevelFromABBToABB(row) as LEVEL_TYPE}
            />
          )}

          <NameLabel>{row.name}</NameLabel>
        </>
      ),

      sorter: true,
      sortOrder: changeSortOrderAntd(queryInfo, "name"),
      defaultSortOrder: "ascend",
      onHeaderCell: onHeaderCell,
      ellipsis: true,
    },

    {
      title: "CP ID",
      key: "accountAdditionalNumber",
      dataIndex: "accountAdditionalNumber",
      hidden: !isDW,
      render: (value, row) => value,
      responsive: ["xxl"],
      width: 90,
    },
    {
      title: "Subscription Type",
      key: "billingType",
      dataIndex: "billingType",
      hidden: !isDW || isPlanHide,
      render: (value, row) => {
        if (row.level === LEVEL_TYPE.CP && row.parentLevel === LEVEL_TYPE.CP) {
          return "-";
        }
        return value === BILLING_TYPE.USAGE && row.isPayment ? "Usage" : "Keys";
      },
      width: 200,
    },
    {
      title: "Required Channels",
      dataIndex: "registeredChannel",
      render: (value, row) => formatNumber(value),
      width: "8%",
      responsive: ["xxl"],
    },
    {
      title: "Activated Channels",
      dataIndex: "activateChannel",
      render: (value, row) => <ActivatedChannel {...row} />,
      width: "8%",
      responsive: isCP ? ["xl"] : ["lg"],
    },
    {
      title: "Channel Expiration",
      key: "channelExpire",
      dataIndex: "channelExpire",
      hidden: isDW,
      render: (value, row) => {
        // if (row.registeredChannel === 0) {
        //   return "-";
        // }
        if (
          !value &&
          (isPlanHide || row.level === LEVEL_TYPE.CP || row.autoEnroll)
        ) {
          return "-";
        }

        return checkSubscriptionStatusFromExpireDate({
          expiredDate: value,
          status: getActivateStatus({
            activateDate: String(new Date()),
            expireDate: value,
          }),
        });
      },
      responsive: ["lg"],
      width: "10%",
      sortOrder: changeSortOrderAntd(queryInfo, "channelExpire"),
      onHeaderCell: onHeaderCell,
      sorter: true,
    },
    {
      title: "Activated Storage (TB)",
      dataIndex: "activateStorage",
      render: (value, row) => {
        const status = getActivateStatus({
          expireDate: row.storageExpire,
        });

        const tooltipText = getExpiredTooltipText(status);
        const formattedStorageExpire = getAccountTimeFormatDate(
          row.storageExpire,
          { ...selectedAccount, timezone: "UTC" }
        );
        const tooltip = tooltipText && (
          <>
            {tooltipText}
            <br />
            {formattedStorageExpire}
          </>
        );

        return (
          <StatusAlertBox
            status={status}
            showAlert={!!tooltip}
            tooltip={tooltip}
          >
            {formatNumber(value)}
          </StatusAlertBox>
        );
      },
      responsive: ["xl"],
      width: "8%",
    },
    {
      title: "Subscription Request",
      dataIndex: "requested",
      render: (value, row, index) => {
        // console.log(value, row);
        if (value && value.length > 0 && row.enableEdit) {
          return (
            <SubscriptionOrgStyled.RequestBox
              fontSize={14}
              onClick={(e) => {
                e.stopPropagation();
                onModalRequest(row);
              }}
              data-testid={`subscription-organization-list-request-box-${index}`}
            >
              <CardSVG />
              {summarizeRequestCounts(value)}
            </SubscriptionOrgStyled.RequestBox>
          );
        }
        return "-";
      },
      width: isBigScreen ? 200 : "10%",
      responsive: ["md"],
      hidden: selectedAccount.isHidden,
    },
    {
      title: "Tax Exempt",
      dataIndex: "taxExempt",
      hidden: !isDW,
      align: "center",
      render: (value, row) => {
        if (
          Boolean(row.taxExempt) &&
          row.level === LEVEL_TYPE.CP &&
          row.parentLevel !== LEVEL_TYPE.CP
        ) {
          const tooltipContents = (
            <SubscriptionOrgStyled.TaxExemptTooltip>
              <div>
                <span>CPP#</span>
                <span>{row.accountNumber}</span>
              </div>
              {row.taxEin && (
                <div>
                  <span>EIN</span>
                  <span>{row.taxEin}</span>
                </div>
              )}
              {row.taxExemptExpiration && (
                <div>
                  <span>Exp.</span>
                  <span>
                    {getAccountTimeFormatDate(row.taxExemptExpiration, {
                      ...selectedAccount,
                    })}
                  </span>
                </div>
              )}
            </SubscriptionOrgStyled.TaxExemptTooltip>
          );
          return (
            <WithTooltip tooltip={tooltipContents}>
              <FaCheck />
            </WithTooltip>
          );
        }
        return null;
      },
      width: 85,
    },
    {
      title: "Auto Enroll",
      dataIndex: "autoEnroll",
      hidden: selectedAccount.isHidden || isDW || isSubCp || isPlanHide,
      render: (value, row) => {
        if (row.level === LEVEL_TYPE.CP) {
          return "-";
        }

        // channelExpire 가 아직 지나지 않아 Usage Plan 전환이 대기중인 상태
        const isPendingSwitch = isPendingUsagePlanSwitch({
          autoEnroll: value,
          channelExpire: row.channelExpire,
        });

        return (
          <SubscriptionOrgStyled.AutoEnrollCell>
            <ToggleComponent
              isToggled={Boolean(value)}
              checked={Boolean(value)}
              // disabled={} usage 등록안될경우
              onChange={(checked, event) => {
                event.stopPropagation();
                onChangeAutoEnroll(row, checked);
              }}
              disabled={!selectedAccount.isUsagePaid}
            />
            {!row.isPayment && isPendingSwitch && (
              <WithTooltip tooltip={USAGE_PLAN_SWITCH_WARNING_TEXT}>
                <IoAlertCircle size={18} />
              </WithTooltip>
            )}
          </SubscriptionOrgStyled.AutoEnrollCell>
        );
      },
      width: 86,
    },
    {
      title: "Status",
      dataIndex: "isSuspended",
      hidden:
        (selectedAccount.isHidden || isDW) &&
        !selectedAccount.userPermission.suspend,
      render: (value, row) => (
        <SuspendButton
          onChangeStatus={onChangeStatus}
          data={row}
          isDisabled={isDW ? !row.enableEdit : false}
        />
      ),
      width: isMobile ? 80 : 120,
      responsive: ["md"],
    },
    {
      key: "buttonColumn",
      title: "",
      render: (value, row, index) => {
        if (row.parentLevel === LEVEL_TYPE.CP && row.level === LEVEL_TYPE.EU) {
          return (
            <RowMobileNoColumn className={`table-button-wrapper`}>
              {(isPlanHide || !row.autoEnroll) && row.enableEdit && (
                <RBACWrapper requiredPermissions={"manageBillingAndLicenses"}>
                  <TableButton
                    label={"Activate"}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsModalActivate(row);
                    }}
                    buttonType="primary"
                    data-testid={`subscription-organization-list-activate-${index}`}
                  />
                </RBACWrapper>
              )}

              <TableButton
                label={"View"}
                onClick={(e) => {
                  e.stopPropagation();
                  debouncedNavigate({
                    to: "/subscription/keys/organization/history",
                    queryString: {
                      ...queryInfo,
                      sortType: "createdAt",
                      rowAccountId: row.accountId,
                      rowAccountNumber: row.accountNumber,
                      rowAccountLevel: row.level,
                      rowParentLevel: row.parentLevel,
                      rowIsUsagePaid: row.autoEnroll,
                    },
                  });
                }}
              />
            </RowMobileNoColumn>
          );
        }
        return null;
      },
      align: "right",
      width: isMobile ? 50 : 190,
      hidden: isDW,
    },
  ];

  const columnTitle = () => {
    const size = 18;
    if (selectedAccount.accountLevel === LEVEL_TYPE.CP) {
      return (
        <button
          className="expand-collapse-icon"
          onClick={onToggleExpandAllInPage}
          disabled={expandableRowKeysInPage.length === 0}
          data-testid="subscription-organization-list-button-toggle-expand-all-in-page"
        >
          {isAllExpandedInPage ? (
            <BsArrowsCollapse size={size} />
          ) : (
            <MdOutlineExpand size={size} />
          )}
        </button>
      );
    }
    return <></>;
  };

  const expandableProps = useMemo<
    ExpandableConfig<SubscriptionOrgListColumnType>
  >(() => {
    return {
      expandRowByClick: false,
      expandedRowKeys,
      onExpand: (expanded: boolean, record: SubscriptionOrgListColumnType) => {
        const row = record as SubscriptionOrgListColumnType & { key?: string };
        const recordRowKey = String(row.key ?? row.accountId);

        setExpandedRowKeys((prev) => {
          if (expanded) {
            return [...prev, recordRowKey];
          }
          return prev.filter((key) => key !== recordRowKey);
        });
      },
      expandedRowRender: (record: SubscriptionOrgListColumnType) => (
        <SubscriptionExpandOrgList
          selectedData={record}
          columns={columns as TableColumnsType<SubscriptionOrgListColumnType>}
          queryInfo={queryInfo}
          expandable={isRowExpandable(record) ? expandableProps : undefined}
        />
      ),

      rowExpandable: (record: SubscriptionOrgListColumnType) =>
        isRowExpandable(record),
      columnTitle: columnTitle(),
    };
  }, [expandedRowKeys, columns, queryInfo]);

  useEffect(() => {
    setButtons(null);
  }, [setButtons]);

  return (
    <>
      <ExpandTableContainer>
        <CommonTable
          columns={columns}
          dataSource={dataSource}
          queryInfo={queryInfo}
          onChangePage={onChangePage}
          loading={isFetching}
          expandable={expandableProps}
        />
      </ExpandTableContainer>
      {cpRequestInfo && (
        <CpRequestCheckModal
          requestInfo={cpRequestInfo}
          onModal={() => onModalRequest()}
          refetch={refetch}
        />
      )}

      {isModalActivate !== null && (
        <SubscriptionActivateModal
          onModal={() => {
            setIsModalActivate(null);
          }}
          row={isModalActivate}
        />
      )}

      {selectedOption && selectedValue !== null && (
        <DeactivateModal
          title={selectedOption?.title ?? ""}
          subTitle={selectedOption?.subTitle ?? ""}
          onDeactive={() =>
            onConfirmSuspend(
              selectedValue.targetAccountId,
              selectedValue.suspensionStatus
            )
          }
          setIsShowModal={() => setSelectedValue(null)}
        />
      )}
      {isUsagePlanModalOpen !== null && (
        <SwitchToUsagePlanModal
          row={isUsagePlanModalOpen.row}
          onModal={() => {
            setIsUsagePlanModalOpen(null);
          }}
          onConfirm={() => {
            executeAutoEnroll(
              isUsagePlanModalOpen.row,
              isUsagePlanModalOpen.checked
            );
          }}
        />
      )}
    </>
  );
}
